<template>
  <div id="dashboard">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="dashboard-container">
          <div v-if="user" class="route-container mb-3 section-heading">
            <div class="text-h4">
              {{ $t('dashboard.welcome') }}
              <span class="font-weight-bold">{{ user.user_name }}</span>
              {{ $t('dashboard.to') }}
              <span v-if="currentChurch[0]"
                ><em>{{ currentChurch[0].churchtitle }}'s</em></span
              >
              {{ $t('dashboard.dashboard') }}
            </div>
            <div class="text-subtitle-1 font-weight-bold">
              {{ $t('dashboard.churchPin') }}
              <span v-if="currentChurch[0]"
                ><span class="text-red">{{ currentChurch[0].cid }}</span></span
              >
            </div>
          </div>
          <div class="subheading">
            <p>{{ $t('dashboard.welcomeMessage') }}</p>
            <p>
              {{ $t('dashboard.description') }}
            </p>
          </div>
          <v-divider class="mt-8 mb-8"></v-divider>
        </div>
        <div v-if="isAuthenticated && checkAuthorization(['superAdmin'])">
          <div class="text-h6 font-weight-bold">REPORTS</div>
          <div class="grid-container" :class="isDarkTheme ? 'dark' : 'light'">
            <v-select
              v-model="selectedMonth"
              :items="translatedMonths"
              label="Select Month"
              variant="solo"
              flat
              density="compact"
              clearable
              :color="isDarkTheme ? '#A5D867' : '#592892'"
              :base-color="isDarkTheme ? '#cecece' : '#000000'"
              class="selected-month-drop-down my-4"
              @update:modelValue="fetchUsersByMonth"
            />
            <template v-if="selectedMonth">
              <div class="label font-weight-bold">
                Activated Users for {{ selectedMonth }} Results
              </div>
              <v-text-field
                v-model="search"
                label="Search"
                single-line
                hide-details
                variant="underlined"
                class="mt-3 custom-search-field"
                append-inner-icon="mdi-magnify"
                clearable
                :class="isDarkTheme ? '#A0C548' : '#592892'"
              >
              </v-text-field>

              <v-btn
                @click="exportUsersToCSV"
                color="rgba(165, 216, 103, 1)"
                class="elevation-0 font-weight-bold my-4"
                :disabled="filteredUsers.length === 0"
              >
                Download CSV
              </v-btn>

              <v-data-table
                :headers="headers"
                :items="filteredUsers"
                :search="search"
                class="elevation-1 users-table"
                :items-per-page="10"
              >
                <template #item.activated="{ item }">
                  <span>{{
                    new Date(item.activated).toLocaleDateString()
                  }}</span>
                </template>
              </v-data-table>
            </template>
          </div>
        </div>
        <div
          id="tutorials-module"
          class="mb-14"
          v-if="
            checkAuthorization(['superAdmin', 'tester']) ||
            (getFeatures && getFeatures.includes('tutorials'))
          "
        >
          <v-divider class="mt-8 mb-8"></v-divider>
          <div class="text-h6 font-weight-bold">TUTORIALS</div>
          <v-row class="grid-row mt-5">
            <v-card v-for="tut in tutorials" :key="tut.name" :cols="tut.flex">
              <iframe
                :src="`https://player.vimeo.com/video/${extractVimeoId(
                  tut.tutorialurl
                )}`"
                width="440"
                height="220"
                frameborder="0"
                allow="autoplay; fullscreen"
                allowfullscreen
                class="pt-5 custom-iframe"
              />

              <v-card-text>
                <v-card-title
                  v-text="tut.tutorialtitle"
                  class="pa-0 font-weight-bold"
                ></v-card-title>
                <div class="text--primary">
                  {{ tut.tutorialsubtitle }}
                </div>
              </v-card-text>
            </v-card>
          </v-row>
        </div>
        <div
          v-if="isAuthenticated && checkAuthorization(['superAdmin', 'admin'])"
          class="grid-container stats-container font-weight-bold"
          :class="isDarkTheme ? 'dark' : 'light'"
        >
          <v-row>
            <v-col class="py-0">
              <div class="text-h6 font-weight-bold">
                {{ $t('dashboard.byTheNumbers') }}
              </div>
            </v-col>
          </v-row>
          <v-row class="stats-row" :class="{ 'flex-column': isMobile }">
            <v-col
              v-if="isAuthenticated && checkAuthorization(['superAdmin'])"
              class="d-flex child-flex flex-column align-center pa-10"
              cols="4"
            >
              <div class="count">
                <v-progress-circular
                  v-if="isloadingChurches"
                  :indeterminate="isloadingChurches"
                  color="#592892"
                ></v-progress-circular>
                <span v-else>{{ this.churches.length }}</span>
              </div>
              <div class="label">Churches</div>
            </v-col>
            <v-col
              class="d-flex child-flex flex-column align-center pa-10"
              cols="4"
            >
              <div class="count">
                <v-progress-circular
                  v-if="isloadingGroups"
                  :indeterminate="isloadingGroups"
                  color="#592892"
                ></v-progress-circular>
                <span v-else>{{ this.groups.length }}</span>
              </div>
              <div class="label">{{ $t('dashboard.groups') }}</div>
            </v-col>
            <v-col
              class="d-flex child-flex flex-column align-center pa-10"
              cols="4"
            >
              <div class="count">
                <v-progress-circular
                  v-if="isloadingUsers"
                  :indeterminate="isloadingUsers"
                  color="#592892"
                ></v-progress-circular>
                <span v-else>{{ this.users.length }}</span>
              </div>
              <div class="label">{{ $t('dashboard.users') }}</div>
            </v-col>
          </v-row>
          <v-row class="stats-row" :class="{ 'flex-column': isMobile }">
            <v-col
              class="d-flex child-flex flex-column align-center pa-10"
              cols="4"
            >
              <div class="count">
                <v-progress-circular
                  v-if="isloadingCourses"
                  :indeterminate="isloadingCourses"
                  color="#592892"
                ></v-progress-circular>
                <span v-else>{{ this.courses.length }}</span>
              </div>
              <div class="label">{{ $t('dashboard.activeCurriculums') }}</div>
            </v-col>
            <v-col
              class="d-flex child-flex flex-column align-center pa-10"
              cols="4"
            >
              <div class="count">
                <v-progress-circular
                  v-if="isloadingCourses"
                  :indeterminate="isloadingCourses"
                  color="#592892"
                ></v-progress-circular>
                <span v-else>{{ this.archivedCourses.length }}</span>
              </div>
              <div class="label">{{ $t('dashboard.archivedCurriculums') }}</div>
            </v-col>
          </v-row>
        </div>
        <div v-if="isAuthenticated && checkAuthorization(['superAdmin'])">
          <v-divider class="mt-8 mb-8"></v-divider>
          <div class="text-h6 font-weight-bold">BY SECTION</div>
          <div>
            <v-tooltip location="top">
              <template #activator="{ props }">
                <span v-bind="props">
                  <v-select
                    v-model="selectedSection"
                    variant="underlined"
                    :items="sections"
                    item-title="title"
                    return-object
                    class=""
                    label="Section Title"
                    :color="isDarkTheme ? '#A5D867' : '#592892'"
                    :base-color="isDarkTheme ? '#cecece' : '#000000'"
                    @update:model-value="getSectionStats(selectedSection)"
                  ></v-select>
                </span>
              </template>
              <span>Select a Section to get Stats</span>
            </v-tooltip>
          </div>
          <div
            class="grid-container stats-container font-weight-bold"
            :class="isDarkTheme ? 'dark' : 'light'"
          >
            <v-row v-if="selectedSection" class="stats-row">
              <v-col
                class="d-flex child-flex flex-column align-center pa-10"
                cols="4"
              >
                <div class="count">
                  <v-progress-circular
                    v-if="isloadingCourses"
                    :indeterminate="isloadingCourses"
                    color="#592892"
                  ></v-progress-circular>
                  <span v-else>{{ this.coursesWithSection.length }}</span>
                </div>
                <div class="label">Courses w/Section</div>
                <v-divider class="mt-8 mb-8"></v-divider>
                <ul class="courses-list font-weight-medium">
                  <li v-for="c in this.coursesWithSection" :key="c.id">
                    {{ c.title }}
                  </li>
                </ul>
              </v-col>
              <v-col
                class="d-flex child-flex flex-column align-center pa-10"
                cols="4"
              >
                <div class="count">
                  <v-progress-circular
                    v-if="isloadingCourses"
                    :indeterminate="isloadingCourses"
                    color="#592892"
                  ></v-progress-circular>
                  <span v-else>{{ this.usersUsingSection.length }}</span>
                </div>
                <div class="label">Current Users</div>
                <v-divider class="mt-8 mb-8"></v-divider>
                <ul class="courses-list font-weight-medium">
                  <li v-for="u in currentUsers" :key="u.uid">
                    {{ u.user_name }} ({{ u.email }})
                  </li>
                </ul>
              </v-col>
              <v-col
                class="d-flex child-flex flex-column align-center pa-10"
                cols="4"
              >
                <div class="count">
                  <v-progress-circular
                    v-if="isloadingCourses"
                    :indeterminate="isloadingCourses"
                    color="#592892"
                  ></v-progress-circular>
                  <span v-else>{{
                    this.usersUsingArchivedSection.length +
                    this.usersUsingSection.length
                  }}</span>
                </div>
                <div class="label">All time Users</div>
                <v-divider class="mt-8 mb-8"></v-divider>
                <ul class="courses-list font-weight-medium">
                  <li v-for="a in allTimeUsers" :key="a.uid">
                    {{ a.user_name }} ({{ a.email }})
                  </li>
                </ul>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import { mapState } from 'vuex'
  import { mapGetters } from 'vuex'
  import { useDisplay } from 'vuetify'
  import { currentTheme } from '@/plugins/vuetify'

  export default {
    name: 'Dashboard',
    components: {
      HowToUseThisPage,
    },
    data() {
      return {
        feedback: null,
        groups: [],
        churches: [],
        users: [],
        courses: [],
        sections: [],
        search: '',
        archivedCourses: [],
        isloadingGroups: false,
        isloadingChurches: false,
        isloadingUsers: false,
        isloadingCourses: false,
        isloadingSections: false,
        selectedSection: null,
        selectedCourse: null,
        coursesWithSection: [],
        archivedCoursesWithSection: [],
        usersUsingCourse: [],
        usersUsingSection: [],
        usersUsingArchivedSection: [],
        selectedGroupsIDs: [],
        courseChurch: null,
        value: 0,
        query: false,
        show: true,
        interval: 0,
        tutorials: [],
        selectedMonth: null,
        filteredUsers: [],
        display: useDisplay(),
        months: [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ],
        options: {
          // responsive: true,
        },
      }
    },
    computed: {
      isDarkTheme() {
        return currentTheme.value === 'dark'
      },
      churchid() {
        return this.$store.getters['AppData/cid']
      },
      ...mapState('UserData', ['user', 'isAuthenticated']),
      ...mapGetters('AppData', ['getFeatures']),
      translatedMonths() {
        return [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ]
      },
      headers() {
        return [
          { title: 'User Name', key: 'user_name' },
          { title: 'Email', key: 'email' },
          { title: 'CID', key: 'cid' },
          {
            title: 'Activated Date',
            key: 'activated',
            sortable: false,
          },
        ]
      },
      currentUsers() {
        return this.users.filter((item) => {
          if (this.usersUsingSection.includes(item.uid)) {
            return item
          }
        })
      },
      allTimeUsers() {
        return this.users.filter((item) => {
          if (this.usersUsingArchivedSection.includes(item.uid)) {
            return item
          }
        })
      },
      currentChurch() {
        if (!this.churches || !this.churches.length) return []
        return this.churches.filter((item) => item.cid === this.churchid)
      },
      isMobile() {
        return this.display.smAndDown.value
      },
    },
    watch: {
      churchid: {
        deep: true,
        handler(event) {
          this.getStats(event)
        },
      },
    },
    methods: {
      extractVimeoId(url) {
        const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)
        return match ? match[1] : ''
      },
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const hasRole = permissions.filter(() =>
            userRoles.some((role) => permissions.includes(role))
          )
          if (hasRole && hasRole.length > 0) return true
        }
        return false
      },
      fetchUsersByMonth() {
        if (!this.selectedMonth) {
          this.filteredUsers = []
          return
        }

        const monthIndex = this.months.indexOf(this.selectedMonth)
        // Start of the month at 00:00:00 hours
        const startOfMonth = new Date(
          new Date().getFullYear(),
          monthIndex,
          1
        ).getTime()
        // End of the month at 23:59:59 hours
        const endOfMonth = new Date(
          new Date().getFullYear(),
          monthIndex + 1,
          0,
          23,
          59,
          59
        ).getTime()

        let usersRef = db.collection('users')
        usersRef = usersRef
          .where('activated', '>=', startOfMonth)
          .where('activated', '<=', endOfMonth)
        usersRef
          .get()
          .then((snapshot) => {
            this.filteredUsers = []
            snapshot.forEach((doc) => {
              let user = doc.data()
              user.id = doc.id
              this.filteredUsers.push(user)
            })
            // console.log('Filtered users:', this.filteredUsers);
          })
          .catch((error) => {
            console.error('Failed to fetch users:', error)
          })
      },
      clearFilters() {
        this.selectedMonth = '' // Reset the selected month
        this.filteredUsers = [] // Clear the filtered users array
        this.search = '' // Clear any search queries
      },
      downloadActivatedUsers() {
        if (!this.filteredUsers.length) return

        const headers = 'User Name,Email,CID,Activated Date\n'
        const rows = this.filteredUsers
          .map(
            (user) =>
              `"${user.user_name}","${user.email}","${user.cid}","${new Date(
                user.activated
              ).toLocaleDateString()}"`
          )
          .join('\n')

        const csvFile = headers + rows
        const blob = new Blob([csvFile], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `ActivatedUsers-${this.selectedMonth}.csv`
        )
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      },
      exportUsersToCSV() {
        if (!this.filteredUsers.length) return

        const headers = `User Name,Email,CID,Activated Date\n`
        const rows = this.filteredUsers
          .map(
            (user) =>
              `"${user.user_name}","${user.email}","${user.cid}","${new Date(
                user.activated
              ).toLocaleDateString()}"`
          )
          .join('\n')

        const csvFile = headers + rows
        const blob = new Blob([csvFile], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `ActivatedUsers-${this.selectedMonth}.csv`
        )
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      },
      getTutorials() {
        setTimeout(() => {
          let tutorialsRef = db.collection('tutorials')
          tutorialsRef = tutorialsRef.where('featured', '==', true)
          tutorialsRef
            .get()
            .then((snapshot) => {
              snapshot.forEach((doc) => {
                let tutorial = doc.data()
                tutorial.id = doc.id
                this.tutorials.push(tutorial)

                this.tutorials.sort((a, b) => (a.order < b.order ? -1 : 0))
              })
            })
            .then(() => {
              // this.isloading = false
            })
        }, 1000)
      },
      getStats(cid) {
        this.groups = []
        this.churches = []
        this.users = []
        this.courses = []
        this.archivedCourses = []
        this.isloadingGroups = true
        this.isloadingChurches = true
        this.isloadingUsers = true
        this.isloadingCourses = true
        this.isloadingSections = true
        this.query = true

        const userRoles = this.$store.getters['UserData/getRoles'] || []
        const isSuper =
          Array.isArray(userRoles) &&
          (userRoles.includes('superAdmin') || userRoles.includes('helpDesk'))

        // delay for User experience
        setTimeout(() => {
          /* First we get the groups */
          let groupsRef = db.collection('groups')
          if (cid) groupsRef = groupsRef.where('cid', '==', cid)
          groupsRef
            .get()
            .then((snapshot) => {
              snapshot.forEach((doc) => {
                let group = doc.data()
                group.group_doc_id = doc.id
                this.groups.push(group)
              })
            })
            .then(() => {
              this.isloadingGroups = false
              this.query = false
              this.show = false

              /* Second we get the churches */
              // Only staff read every church (firestore.rules)
              let churchesRef = db.collection('churches')
              churchesRef = churchesRef.where('archive', '==', false)
              if (!isSuper) churchesRef = churchesRef.where('cid', '==', cid || '')
              ;(isSuper || cid ? churchesRef.get() : Promise.resolve([]))
                .then((snapshot) => {
                  var g = []
                  snapshot.forEach((doc) => {
                    let church = doc.data()
                    church.id = doc.id
                    if (!church.archive) {
                      g.push(church)
                    }
                  })
                  this.churches = g
                })
                .then(() => {
                  this.isloadingChurches = false

                  /* Third we get the users */
                  let usersRef = db.collection('users')
                  if (cid) usersRef = usersRef.where('cid', '==', cid)
                  usersRef
                    .get()
                    .then((snapshot) => {
                      snapshot.forEach((doc) => {
                        let user = doc.data()
                        user.id = doc.id
                        this.users.push(user)
                      })
                    })
                    .then(() => {
                      this.isloadingUsers = false

                      /* Fourth we get the courses */
                      let coursesRef = db.collection('courses')
                      if (cid) coursesRef = coursesRef.where('cid', '==', cid)
                      coursesRef
                        .get()
                        .then((snapshot) => {
                          snapshot.forEach((doc) => {
                            let course = doc.data()
                            course.id = doc.id
                            if (course.archive && !course.base)
                              this.archivedCourses.push(course)
                            else if (!course.archive && !course.base)
                              this.courses.push(course)
                            // else
                            // this.archivedCourses.push(course)
                            // this.courses.push(course)
                          })
                        })
                        .then(() => {
                          // console.log(this.courses)
                          this.isloadingCourses = false

                          /* Fifth we get the sections */
                          this.sections = []
                          if (!isSuper) {
                            this.isloadingSections = false
                            return
                          }
                          let sectionsRef = db.collection('sections')
                          sectionsRef
                            .get()
                            .then((snapshot) => {
                              snapshot.forEach((doc) => {
                                let section = doc.data()
                                section.id = doc.id
                                // console.log(section)
                                this.sections.push(section)
                              })
                            })
                            .then(() => {
                              // console.log(this.courses)
                              this.isloadingSections = false
                            })
                        })
                    })
                })
            })
        }, 1000)
      },
      getSectionStats(selectedSection) {
        this.usersUsingSection = []
        this.usersUsingArchivedSection = []

        // get all courses using this section
        this.coursesWithSection = this.courses.filter((obj) => {
          return obj.sections.some(function (obj2) {
            return obj2.id == selectedSection.id
          })
        })

        // get all archived courses using this section
        this.archivedCoursesWithSection = this.archivedCourses.filter((obj) => {
          return obj.sections.some(function (obj2) {
            return obj2.id == selectedSection.id
          })
        })

        const theGroups = this.groups

        // get all members using section
        this.coursesWithSection.filter((item) => {
          // get all the IDs of the selected groups
          for (const selectedID of item.selectedGroup) {
            // get all the selected groups
            for (const selectedGroup of theGroups) {
              if (selectedGroup.group_doc_id == selectedID.id) {
                // get all the member ids that are in the group
                for (const members of selectedGroup.members) {
                  // create an array of all the member ids
                  this.usersUsingSection.push(members)
                }
              }
            }
          }
        })

        // get all members using archived section
        this.archivedCoursesWithSection.filter((item) => {
          // get all the IDs of the selected groups
          for (const selectedID of item.selectedGroup) {
            // get all the selected groups
            for (const selectedGroup of theGroups) {
              if (selectedGroup.group_doc_id == selectedID.id) {
                // get all the member ids that are in the group
                for (const members of selectedGroup.members) {
                  // create an array of all the member ids
                  this.usersUsingArchivedSection.push(members)
                }
              }
            }
          }
        })
      },
    },
    created() {
      this.getStats(this.churchid)

      this.getTutorials()
    },
  }
</script>

<style scoped>
  #dashboard :deep(.v-container) {
    width: 95%;
    max-width: 1440px;
    padding: 0;
  }
  #dashboard :deep(.v-container) {
    padding-bottom: 50%;
  }
  #dashboard :deep(.v-divider) {
    width: 100%;
  }
  .subheading {
    max-width: 600px;
  }
  .count {
    font-size: 125px;
    line-height: 1;
    flex: 0 0 auto;
  }
  .label {
    font-size: 20px;
    flex: 0 0 auto;
  }
  .theme--light .label {
    color: #383935;
  }

  .theme--dark .label {
    color: rgb(206, 206, 206);
  }

  .courses-list {
    /* list-style: none; */
    padding-left: 7px;
    max-height: 525px;
    overflow: scroll;
  }
  #tutorials-module .grid-row > div {
    /* flex: auto; */
    display: flex;
    flex-direction: column;
  }
  #tutorials-module .grid-row {
    gap: 30px;
  }
  .selected-month-drop-down {
    max-width: 300px;
  }

  .custom-iframe {
    text-align: center;
  }
  .custom-iframe :deep(iframe) {
    max-width: 90%;
  }
  .custom-search-field :deep(.v-icon) {
    opacity: 1;
  }
</style>
